<template>
	<v-container>
		<v-card id="risk-description">
			<v-col>
				<h1>Risk Description : <span style="font-size: 0.8em;">{{ this.$store.getters.selectedTitle }}</span>
				</h1>
			</v-col>
			<v-col>
				<p style="font-size: 16px; margin-left: 10px; margin-right: 10px; text-align: justify;">Using this
					table, you can plot your risks into the created risk matrix, where each risk is displayed according
					to its probability and consequence. You can add, edit, or delete risks as needed. Additionally, you
					can update the status of risks following the implementation of action plans and review the
					historical record of each risk for comprehensive tracking and analysis.</p>
				<p style="font-size: 16px; margin-left: 10px; margin-right: 10px; text-align: justify;">In the table the
					risk will be defined by the level of consequence and probability you evaluate it too. Consequence
					and probability level scored as 1 in the risk definition table equals point 1-5 in the risk matrix
					and risk description table, level 2 equals 6-10, level 3 equals 11-15, level 4 equals 16-20 and
					level 5 equals 21-25. This scale from 1-25 gives you the opportunity to have several risks placed in
					the same risk level per risk matrix. We advise you to consider 1-5 probability and consequence score
					as level 1 and not to dig deeper into the scale than that. The reason we have a score of 1-25 is a
					technical interpretation of the scale 1-5.</p>
			</v-col>
			<v-col class="addnewbutton">
				<v-btn color="primary" :to="{ path: '/csr/riskmatrix/add', query: { matrixID: selectedId } }"
					:disabled="isButtonDisabled">
					<v-icon left>fas fa-plus</v-icon>NEW
				</v-btn>
			</v-col>
			<v-card-title>
				<v-text-field v-model="search" append-icon="mdi-magnify" label="Search" single-line
					hide-details></v-text-field>
			</v-card-title>
			<v-data-table theme="--light" :headers="headers" :items="connections" :search="search" :items-per-page="20"
				class="light table" :footer-props="{
					'items-per-page-options': [5, 10, 15, 20],
				}">
				<template v-slot:header="{ headers }">
					<thead>
						<tr>
							<th v-for="header in headers" :key="header.text" class="custom-header"
								style="text-align: left;">
								{{ header.text }}
							</th>
						</tr>
					</thead>
				</template>
				<template v-slot:item.Risk="{ item }">
					<td style="text-align: center;">
						<span style="text-align: center;">{{ item.Risk }}</span>
					</td>
				</template>
				<template v-slot:item.RiskDescription="{ item }">
					<td style="text-align: justify;">
						<span>{{ item.RiskDescription }}</span>
					</td>
				</template>
				<template v-slot:item.TypesOfRisk="{ item }">
					<td style="text-align: center;">
						<span>{{ item.TypesOfRisk }}</span>
					</td>
				</template>
				<template v-slot:item.RiskOwner="{ item }">
					<td style="text-align: center;">
						<span>{{ item.RiskOwner }}</span>
					</td>
				</template>
				<template v-slot:item.Created="{ item }">
					<td style="text-align: center;">
						<span>{{ item.Created }}</span>
					</td>
				</template>
				<template v-slot:item.Creator="{ item }">
					<td style="text-align: center;">
						<span>{{ item.Creator }}</span>
					</td>
				</template>
				<template v-slot:item.Consequence="{ item }">
					<td style="text-align: center;">
						<span>{{ item.Consequence }},{{ item.Probability }}</span>
					</td>
				</template>

				<template v-slot:item.editbutton="{ item }">
					<td class="small_cell" style="width: 30px; padding-right: 0px">
						<v-menu offset-y>
							<template v-slot:activator="{ on, attrs }">
								<div class="buttonfeather">
									<v-btn v-bind="attrs" v-on="on" small elevation="0" text>
										<v-icon>fas fa-ellipsis-v</v-icon>
									</v-btn>
								</div>
							</template>
							<v-list>
								<v-list-item @click="editItem(item)" :disabled="!isActionAllowed">
									<v-list-item-title>Edit</v-list-item-title>
								</v-list-item>
								<v-list-item @click="deleteItem(item)">
									<v-list-item-title>Delete</v-list-item-title>
								</v-list-item>
								<v-list-item @click="updateItem(item)" :disabled="!isActionAllowed">
									<v-list-item-title>Update Status</v-list-item-title>
								</v-list-item>
								<v-list-item @click="historyItem(item)">
									<v-list-item-title> History</v-list-item-title>
								</v-list-item>
							</v-list>
						</v-menu>
					</td>
				</template>
			</v-data-table>
			<v-dialog v-model="dialogDelete" max-width="500px">
				<v-card>
					<v-card-title>Are you sure you want to remove this Risk?</v-card-title>
					<v-card-text>Once it is removed, you will not able to track your
						risk</v-card-text>
					<v-card-actions>
						<v-btn color="primary" text @click="dialogDelete = false">Close</v-btn>
						<v-btn color="error" text @click="removeRow">Delete</v-btn>
					</v-card-actions>
				</v-card>
			</v-dialog>
			<v-dialog v-model="dialogHistory" max-width="1000px">
				<v-card>
					<div style="text-align: right;">
						<v-btn color="red" text @click="dialogHistory = false"><v-icon>fa-solid
								fa-rectangle-xmark</v-icon></v-btn>
					</div>
					<v-col align="center">
						<h2>History of the risk updates</h2>
						<p><i>This is a log of status changes made to this risk.</i></p>
					</v-col>
					<v-col justify-center cols="12">
						<v-data-table hide-default-footer theme-- light :headers="statusHeaders" :items="tableData"
							class="light table mx-4 completed-table" :footer-props="{
								'items-per-page-options': [5, 10, 15],
							}">
							<template v-slot:item.Status="{ item }">
								<span :class="{
										'green--text': item.Status === 'Low',
										'red--text': item.Status === 'High',
										'yellow--text': item.Status === 'Moderate',
									}">{{ item.Status }}</span>
							</template>
						</v-data-table>
					</v-col>
					<br />
				</v-card>
			</v-dialog>
		</v-card>
	</v-container>
</template>

<script>
import { riskAPI } from "../api/csr/risk"
import LoadingState from "../consts/LoadingState"
import { mapGetters } from "vuex"

export default {
	data() {
		return {
			ID: null,
			clientID: null,
			dialogDelete: false,
			dialogHistory: false,
			connections: [],
			avgScore: [],
			search: "",
			headers: [
				{
					text: "No",
					align: "start",
					value: "no",
					sortable: false,
					width:"5%",
				},
				{
					text: "Risk",
					align: "center",
					value: "Risk",
					sortable: false,
					width:"5%",
				},
				{
					text: "Risk Description",
					align: "center",
					value: "RiskDescription",
					sortable: false,
					width:"40%",
				},
				{
					text: "Risk Type",
					align: "center",
					value: "TypesOfRisk",
					sortable: false,
					width:"15%",
					
				},
				{
					text: "Risk Owner",
					value: "RiskOwner",
					sortable: false,
				},
				{
					text: "Created",
					value: "Created",
					sortable: false,
					width:"12%",

				},
				{
					text: "Creator",
					align: "center",
					value: "Creator",
					sortable: false,
				},
				{
					text: "Cons,Prob",
					value: "Consequence",
					sortable: false,
				},

				{
					value: "editbutton",
					sortable: false,
				},
			],
			statusHeaders: [
				{
					text: "Creator",
					value: "Creator",
					sortable: false,
					class: "secondary--text",
				},
				{
					text: "Status",
					value: "Status",
					sortable: false,
					class: "secondary--text",
				},
				{
					text: "Timestamp",
					value: "Timestamp",
					sortable: false,
					class: "secondary--text",
				},
				{
					text: "Comment",
					value: "Comment",
					sortable: false,
					class: "secondary--text",
				},
				{
					text: "Consequence",
					value: "Consequence",
					sortable: false,
					class: "secondary--text",
				},
				{
					text: "Probability",
					value: "Probability",
					sortable: false,
					class: "secondary--text",
				},
			],

			tableData: [],
		}
	},
	computed: {
		...mapGetters([
			"selectedId",
			"selectedYear",
			"selectedTitle",
			"selectedDescription",
			"selectedStatus",
		]),
		isActionAllowed() {
			return this.selectedStatus === "Active"
		},
		isButtonDisabled() {
			return !this.isActionAllowed || this.selectedId === null
		},
	},
	watch: {
		selectedId(newVal) {
			console.log("Selected ID changed Risk Description:", newVal)
			this.loadItems(newVal)
			if(newVal==null){
				this.isActionAllowed()
			}
		},
	},
	mounted() {
		this.loadItems(this.selectedId)
	},
	methods: {
		loadItems(id) {
			this.connections = []
			this.$store.dispatch(
				"loading/" + LoadingState.ACTION_START_LOADING,
				null,
				{ root: true }
			)
			riskAPI
				.getAll(id)
				.then((data) => {
					let editbutton = ""
					let no = 0

					for (let entry of data[0]) {
						this.connections.push({
							no: ++no,
							ID: entry.ID,
							Risk: entry.Risk,
							RiskDescription: entry.RiskDescription,
							TypesOfRisk: entry.TypesOfRisk,
							RiskOwner: entry.RiskOwner,
							Created: entry.Created.split("T")[0],
							Creator: entry.Creator,
							Consequence:
								entry.UpdatedConsequence != null
									? entry.UpdatedConsequence
									: entry.Consequence,
							Probability:
								entry.UpdatedProbability != null
									? entry.UpdatedProbability
									: entry.Probability,
							editbutton: editbutton,
							FirstConsequence: entry.Consequence,
							FirstProbability: entry.Probability,
							UpdatedConsequence: entry.UpdatedConsequence,
							UpdatedProbability: entry.UpdatedProbability,
							UpdatedDate:
								entry.UpdatedDate != null
									? entry.UpdatedDate.split("T")[0]
									: entry.UpdatedDate,
							CommentforUpdate: entry.CommentforUpdate,
						})
					}
					// Can't make a check inside for loop (eslint), have to remove it outside. Not a good fix.
					/*					this.connections = this.connections.filter(function (obj) {
						return obj.clientID !== 1 // clientID 1 is the user (client accessing the portal).
					}) */
					// Used by old method of showing sustainability
					//this.averageScore() // Calculating average score of all suppliers
					this.$store.dispatch(
						"loading/" + LoadingState.ACTION_END_LOADING,
						null,
						{ root: true }
					)
				})
				.catch((error) => {
					this.$store.dispatch(
						"loading/" + LoadingState.ACTION_SET_ERROR,
						error,
						{ root: true }
					)
				})
		},
		calculateStatus(probability, consequence) {
			if (
				(probability <= 5 && consequence <= 20) ||
				(probability <= 10 && consequence <= 10) ||
				(probability <= 20 && consequence <= 5)
			) {
				return "Low"
			} else if (
				(probability > 20 && consequence > 10) ||
				(probability > 15 && consequence > 15) ||
				(probability > 10 && consequence > 20)
			) {
				return "High"
			} else {
				return "Moderate"
			}
		},
		deleteItem(item) {
			this.itemToDelete = item
			this.dialogDelete = !this.dialogDelete
		},
		historyItem(item) {
			this.itemToHistory = item
			this.dialogHistory = !this.dialogHistory

			// Start with the first row
			let newData = [
				{
					Creator: item.Creator,
					Status: this.calculateStatus(
						item.FirstProbability,
						item.FirstConsequence
					),
					Timestamp: item.Created,
					Comment: item.RiskDescription,
					Consequence: item.FirstConsequence,
					Probability: item.FirstProbability,
				},
			]

			// Add the second row if item.UpdatedConsequence is not null
			if (item.UpdatedConsequence != null) {
				newData.push({
					Creator: item.Creator,
					Status: this.calculateStatus(
						item.UpdatedProbability,
						item.UpdatedConsequence
					),
					Timestamp: item.UpdatedDate,
					Comment: item.CommentforUpdate,
					Consequence: item.UpdatedConsequence,
					Probability: item.UpdatedProbability,
				})
			}

			// Update tableData
			this.$set(this, "tableData", newData)
		},

		updateItem(item) {
			this.$router.push({
				path: "/csr/riskmatrix/update",
				query: {
					itemId: item.ID,
				},
			})
		},
		editItem(item) {
			this.$router.push({
				path: "/csr/riskmatrix/edit",
				query: {
					itemId: item.ID,
				},
			})
		},
		removeRow() {
			this.dialogDelete = false
			let index = this.connections.indexOf(this.itemToDelete)
			this.$store.commit("setSelectedNewstate", true)
			this.$store.dispatch(
				"loading/" + LoadingState.ACTION_START_LOADING,
				null,
				{ root: true }
			)

			riskAPI
				.deleteRisk(this.itemToDelete.ID)
				.then(() => {
					this.$store.dispatch(
						"loading/" + LoadingState.ACTION_LOADING_SUCCESS,
						"Risk item was successfully removed from the list.",
						{ root: true }
					)
					
					this.connections.splice(index, 1)
					window.location.href = "/csr/riskmatrix/riskpage"
				})
				.catch((error) => {
					this.$store.dispatch(
						"loading/" + LoadingState.ACTION_SET_ERROR,
						error,
						{ root: true }
					)
				})
			
		},
	},
}
</script>

<style>
.v-progress-circular {
	margin: 0rem;
}

.addnewbutton {
	display: flex;
	justify-content: flex-end;
}

.custom-header {
	font-size: 100px;
}

.v-data-table .v-data-table-header tr th {
	font-size: 15px !important;
	color: #217b7f !important;
}

.buttonfeather {
	display: flex;
}

.small_cell {
	width: 20px !important;
	padding-right: 0px;
}
</style>
